import {
  CUE_WINDOW_SECONDS,
  type DetectedCue,
  detectEndOfVisitCues,
  foldCueText,
} from "@rosc/contracts";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { getMacros } from "@/services/macros";
import { useAuthStore } from "@/store/auth.store";
import { useRecordingInstructionsStore } from "@/store/recordingInstructions.store";
import { useTranscriptionStreamingStore } from "@/store/transcriptionStreaming.store";

// A little before the window, so a sentence that started earlier is judged
// whole (the detector keeps only the last CUE_WINDOW_SECONDS).
const LEAD_SECONDS = 20;

/**
 * Saved shortcuts the clinician said ("insert …") in the last minute of the
 * live transcript, with the server's rules, minus the ones dismissed. Empty
 * without a live transcript.
 */
export const useLiveShortcutCues = (
  enabled: boolean,
  recordingTime: number,
): DetectedCue[] => {
  const uid = useAuthStore((state) => state.user?.uid);
  const items = useTranscriptionStreamingStore(
    (state) => state.transcriptionStreamText,
  );
  const skipped = useRecordingInstructionsStore((state) => state.skipped);
  const shortcuts = useQuery({
    queryKey: ["recording-shortcuts", uid],
    enabled: enabled && !!uid,
    retry: false,
    staleTime: 5 * 60 * 1000,
    queryFn: () => getMacros(uid),
  });
  return useMemo(() => {
    const saved = (shortcuts.data ?? []).filter(
      (macro) => macro.name.trim() && macro.details.trim(),
    );
    if (!enabled || !saved.length) return [];
    const from = recordingTime - CUE_WINDOW_SECONDS - LEAD_SECONDS;
    const recent = items.filter((item) => item.startTime >= from);
    const first = recent[0];
    if (!first) return [];
    const dismissed = new Set(skipped.map((name) => foldCueText(name)));
    return detectEndOfVisitCues(
      recent.map((item) => item.text).join("\n"),
      saved.map((macro) => ({ trigger: macro.name })),
      { durationSeconds: Math.max(1, recordingTime - first.startTime) },
    ).filter((cue) => !dismissed.has(foldCueText(cue.trigger.trim())));
  }, [enabled, shortcuts.data, items, recordingTime, skipped]);
};
